import React, { useState } from "react"
import { Link } from "gatsby"
import Image from '../components/image';
import Layout from "../components/layout"
import SEO from "../components/seo"
import { Grid, Avatar, Tooltip, Button, List, ListItem, Typography, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Fab } from "@material-ui/core";
import { Assistant } from "@material-ui/icons";
import { DropzoneArea } from 'material-ui-dropzone';
import { useSnackbar } from 'notistack';
import { ClockLoader, MoonLoader } from "react-spinners";
import MoreInfo from '../components/moreInfo';

import '../styles/avatar-generator.scss';
import allChars from '../data/characters.json';
import avatarGIF from '../images/avatar-gen.gif';
import faceMorphing from '../images/face-morphing.gif';
import base1 from '../images/avatar-generator/base-1.jpg';
import base2 from '../images/avatar-generator/base-2.jpg';
import base3 from '../images/avatar-generator/base-3.jpg';
import base4 from '../images/avatar-generator/base-4.jpg';
import base5 from '../images/avatar-generator/base-5.jpg';
import base6 from '../images/avatar-generator/base-6.jpg';
import base7 from '../images/avatar-generator/base-7.jpg';
import base8 from '../images/avatar-generator/base-8.jpg';

const intendedChars = ['steve', 'robin', 'eleven', 'jim', 'will', 'billy', 'max'];
let chars = allChars.filter((c) => (intendedChars.includes(c.id)) ? c : null)
chars = chars.sort((a, b) => intendedChars.indexOf(a.id) - intendedChars.indexOf(b.id))

const onClick = (files, tries, setOpen, setTries, notif) => {
  const file = files[0];
  if (!file) {
    notif("No files selected!", { variant: "error" });
    return;
  }
  if (tries > 3) {
    notif("You've tried more than 3 times", { variant: "warning" });
    notif("Come back just a few seconds later", { variant: "default" });
    if (tries === 4) {
      setTimeout(() => {
        setTries(0);
      }, 10000);
    }
    return;
  }
  setOpen(true)
}

const doProcess = async (file, type, setOpen, setLoading, setData, setTries, notif) => {
  notif("Please wait ...", { variant: "info" });
  setLoading(true);
  const scrollInterval = setInterval(() => {
    const loading = document.getElementById("loading");
    if (loading) {
      clearInterval(scrollInterval);
      window.scrollTo(0, loading.offsetTop);
    }
  }, 10);
  if (typeof (window) !== undefined) {
    setOpen(false);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("type", type);
    const agAPI = window.agAPI;
    try {
      const res = await fetch(agAPI, { method: "POST", credentials: "same-origin", body: formData })
      const resJSON = await res.json();
      const { message, result, status } = resJSON;
      if (status.code === 200) {
        setData(result);
        setTries((tries) => tries > 4 ? tries : tries + 1);
        notif("Processed Successfully", { variant: "success" });
      }
      else {
        (message || []).forEach((msg) => {
          notif(msg, { variant: "error" });
        });
        if (status.code === 500)
          notif("Maybe face not detected", { variant: "error" });
      }
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
      notif("Something went wrong!", { variant: "error" });
      notif("Try again later", { variant: "default" });
    }
    finally {
      return;
    }
  }
  notif("No API FOUND!", { variant: "error" });
}

const Render = () => {
  const [files, setFiles] = useState([]);
  const [type, setType] = useState(1);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [tries, setTries] = useState(0);
  const { enqueueSnackbar: notif } = useSnackbar();
  const mediaURL = (typeof (window) !== `undefined`) ? window.mediaURL : "";
  const { image_path } = (data || {});

  return (
    <div id="AvatarGenerator">
      <div className="head">
        <img src={avatarGIF} alt="stranger things face similarity" />
        <h1>Check out the <b>avatar-generation</b> process and generates artistic avatars blended with your face</h1>
      </div>
      <div>
        <h2>You can generate avatars based on these characters:</h2>
        <Grid justify="center" container component="ul" spacing={1}>
          {
            chars.map((char) => (
              <Grid key={char.row} item component="li" xs={6} sm={3} md={2} lg={1} xl={1}>
                <Tooltip title={char.name} arrow placement="top">
                  <Avatar style={{ width: 100, height: 100, margin: "0 auto", boxShadow: "0 0 6px #6b6b6b" }}>
                    <Image name={char.avatar} style={{ width: "100%", height: "100%" }} />
                  </Avatar>
                </Tooltip>
              </Grid>
            ))
          }
        </Grid>
        <div><b>This process will create a blended avatar photo of ST characters with the recognised face of the image you'll upload.</b></div>
      </div>
      <br />
      <h3 className="ai-ml"><Assistant /> Powered By Artificial Intelligence & Machine Learning</h3>
      {
        !loading && !data &&
        (
          <Grid>
            <div className="help">
              <Grid justify="center" container spacing={4}>
                <Grid item xs={10} sm={10} md={9} lg={8} xl={10}>
                  <figure className="gif">
                    <img src={faceMorphing} alt="face morphing" />
                    <figcaption>
                      <a href="https://dribbble.com/shots/2153975-Face-Morphing-GIF">https://dribbble.com/shots/2153975-Face-Morphing-GIF</a>
                    </figcaption>
                  </figure>
                </Grid>
              </Grid>
            </div>
            <div className="tips">
              <Typography variant="h4" component="h3">Tips:</Typography>
              <List>
                <ListItem>Your photo should <u>not</u> have too much background <b>textures</b></ListItem>
                <ListItem>The dimension of your photo is better to be <u>more than</u>&nbsp;<b>200x200</b></ListItem>
                <ListItem>Your photo <u>should not</u> be cropped to contain just the <b>face</b>. The process will recognise the face landmark itself!</ListItem>
                <ListItem>Your photo should contain <b>one face</b> because the process will consider the first face that is recognised!</ListItem>
              </List>
            </div>
            <div className="type">
              <h4>
                Choose the one you wanna be used as a base:
              </h4>
              <Grid className="type-grid" container spacing={2}>
                {
                  [
                    { img: base3, idx: 2 },
                    { img: base8, idx: 7 },
                    { img: base7, idx: 6 },
                    { img: base2, idx: 1 },
                    { img: base4, idx: 3 },
                    { img: base5, idx: 4 },
                    { img: base6, idx: 5 },
                    { img: base1, idx: 0 },
                  ]
                    .map((b, idx) => (
                      <Grid className="type-item" key={idx} item xs={6} sm={3} md={3}>
                        <Fab variant="round" className={b.idx + 1 === type ? "fab active" : "fab"} onClick={() => setType(b.idx + 1)}>
                          <Avatar className="avatar" src={b.img} />
                        </Fab>
                      </Grid>
                    ))
                }
              </Grid>
            </div>
            <div className="dropzone-wrapper">
              <DropzoneArea
                dropzoneText="Drag and drop an image here or click"
                filesLimit={1}
                maxFileSize={3000000}
                dropzoneClass="dropzone"
                dropzoneParagraphClass="text"
                previewGridClasses={{ container: "container", item: "item", image: "image" }}
                acceptedFiles={["image/*"]}
                onChange={(files) => setFiles(files)}
              />
            </div>
            <Button
              fullWidth
              size="large"
              color="secondary"
              variant="contained"
              onClick={() => onClick(files, tries, setOpen, setTries, notif)}
            >
              Run Process
            </Button>
          </Grid>
        )
      }
      {
        loading &&
        (
          <div id="loading" className="loading">
            <ClockLoader
              color="#C11B1F"
              size={250}
              loading
            />
            <div className="running">
              <MoonLoader size={20} />
              <h3 style={{ textAlign: "center" }}>Running On Server</h3>
              <MoonLoader size={20} />
            </div>
            <Typography variant="h6">Please Wait</Typography>
            <Typography variant="subtitle1">It might take a minute or two</Typography>
          </div>
        )
      }
      {
        !loading && data &&
        (
          <div className="result">
            <Button fullWidth color="secondary" variant="outlined" onClick={() => setData(null)}>Try Another One</Button>
            <br />
            <br />
            <Typography variant="subtitle1">
              The <b>face</b> in your own photo should have <b>proper angles and perspective</b> ...
            </Typography>
            <Typography variant="subtitle2">
              Otherwise, the result could be terribly unpleasant!
            </Typography>
            <br />
            <Grid justify="center" container spacing={2}>
              <Grid item xs={12} sm={12} md={10} lg={8}>
                <img className="generated-image" src={`${mediaURL}/${image_path}`} alt="generated" />
              </Grid>
            </Grid>
          </div>
        )
      }
      <br />
      <br />
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        maxWidth="md"
        fullWidth
      >
        <DialogTitle id="alert-dialog-title">{"Run the Avatar Generator?"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            You are about to upload your photo to get the blended avatar.
            <br />
            The photo will be collected <b>safely</b> on our servers and will be deleted after the process.
            <br />
            The resulting photo will be deleted too within 24 hours from our servers.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={() => doProcess(files[0], type, setOpen, setLoading, setData, setTries, notif)} color="secondary">
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </div >
  );
}

const description = `Take the process of generating stranger things avatar blended with your face. this will mix your photo with one of the stranger things people on demand`;
const keywords = [
  "stranger things stuffs",
  "stranger things",
  "nerdy avatar generator",
  "avatar generation",
  "ST",
  "Eleven",
  "Jane Hopper",
  "Will",
  "Jim Hopper",
  "Mad Max",
  "Billy",
  "Robin",
  "Steve"
]

const mi = {
  why: `<ul>
    <li>To have some fun</li>
    <li>To make some learnings -- our learning, not machine learning :)</li>
    <li>To make some artistic avatars</li>
  </ul>`,
  method: `<ul>
    <li>Computer vision approach (image processing)</li>
  </ul>
  <hr />
  <div>Stages:</div>
  <br />
  <ul>
  <li>Face Alignment</li>
  <li>Facial Landmark Detection</li>
  <li>Find Convex Hull</li>
  <li>Delaunay Triangulation</li>
  <li>Affine Warp Triangles</li>
  <li>Seamless Cloning</li>
  <li>Make Artistic</li>
  </ul>
  <hr />
  <div>You can see how to make an artistic image in the below youtube video:</div>
  <div>(Just one approach of making artistic has been mentioned)</div>
  <br />
  <div>
  <iframe width="100%" height="300" src="https://www.youtube-nocookie.com/embed/q4ME_sUbbH0" frameborder="0" allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>
  </div>
  `,
  accuracy: `<div>
    Can't say
  </div>`,
  version: `<div>v0.1.2</div>`,
}

const AvatarGenerator = () => (
  <Layout navNumber={2}>
    <SEO
      title="Put Your Face Into Stranger Things Casts Avatar - Avatar Generator"
      description={description}
      keywords={keywords.join(",")}
    />
    <Render />
    <div>
      <MoreInfo
        why={mi.why}
        method={mi.method}
        accuracy={mi.accuracy}
        version={mi.version}
      />
    </div>
    <Link to="/">Go back to the homepage</Link>
  </Layout>
)

export default AvatarGenerator
